export const INTERVIEW_ANSWER_RECOVERY_VERSION = 1 as const;
export const INTERVIEW_ANSWER_RECOVERY_TTL_MS = 30 * 60 * 1000;
export const INTERVIEW_ANSWER_RECOVERY_PREFIX = 'tema:interview-answer-recovery:v1:';

export type InterviewAnswerRecoveryState = 'pending' | 'uncertain';

export type InterviewAnswerRecoveryPayload = {
  question_id: number;
  answer_text: string;
  raw_answer?: string;
  transcript_reviewed?: boolean;
  transcript_risk_acknowledged?: boolean;
  transcript_quality_score?: number | null;
  transcript_review_reason?: string | null;
  transcript_quality_flags?: string[];
  transcript_risky_shift_detected?: boolean;
  transcript_prefer_raw?: boolean;
  transcript_mode?: string | null;
  client_attempt_id: string;
  video_url?: string | null;
  video_mime_type?: string | null;
  video_size_bytes?: number | null;
  video_duration_seconds?: number | null;
  time_spent_seconds: number;
  auto_submit?: boolean;
  conversation_mode?: boolean;
  seamless_mode?: boolean;
};

export type PendingInterviewAnswerRecovery = {
  version: typeof INTERVIEW_ANSWER_RECOVERY_VERSION;
  state: InterviewAnswerRecoveryState;
  user_id: string;
  interview_id: number;
  job_id: number;
  outsource_id: number;
  question_id: number;
  client_attempt_id: string;
  payload: InterviewAnswerRecoveryPayload;
  created_at: number;
  updated_at: number;
  expires_at: number;
};

export type PendingInterviewAnswerRecoveryScope = {
  userId: string;
  interviewId: number;
  jobId: number;
  outsourceId: number;
};

export type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

export type PendingRecoveryReadResult = {
  status: 'valid' | 'missing' | 'expired' | 'invalid';
  envelope: PendingInterviewAnswerRecovery | null;
  scope?: {
    userId: string;
    interviewId: number;
    jobId: number;
    outsourceId: number;
    updatedAt: number;
  } | null;
};

export type PendingRecoveryBackendSnapshot = {
  questionIds: number[];
  answeredQuestionIds: number[];
  currentQuestionId: number | null;
  interviewCompleted: boolean;
  canFinish: boolean;
};

export type PendingRecoveryRestoreDecision = {
  action: 'reconcile' | 'stale_success' | 'discard';
  consumeNoAnswerAttempt: false;
};

const normalizeIdentifier = (value: unknown): string => String(value ?? '').trim();

const normalizePositiveInteger = (value: unknown): number => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : 0;
};

const getDefaultStorage = (): RecoveryStorage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const resolveStorage = (storage?: RecoveryStorage): RecoveryStorage | null => storage || getDefaultStorage();

const safeParse = (value: string | null): any | null => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const listStorageKeys = (storage: RecoveryStorage): string[] => {
  try {
    return Array.from({ length: storage.length }, (_, index) => storage.key(index))
      .filter((key): key is string => Boolean(key));
  } catch {
    return [];
  }
};

const removeStorageKey = (storage: RecoveryStorage | null, key: string) => {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {}
};

const isRecoveryEnvelopeShapeValid = (value: any): value is PendingInterviewAnswerRecovery => {
  if (!value || typeof value !== 'object') return false;
  if (value.version !== INTERVIEW_ANSWER_RECOVERY_VERSION) return false;
  if (value.state !== 'pending' && value.state !== 'uncertain') return false;

  const userId = normalizeIdentifier(value.user_id);
  const interviewId = normalizePositiveInteger(value.interview_id);
  const jobId = normalizePositiveInteger(value.job_id);
  const outsourceId = normalizePositiveInteger(value.outsource_id);
  const questionId = normalizePositiveInteger(value.question_id);
  const clientAttemptId = normalizeIdentifier(value.client_attempt_id);
  const payload = value.payload;

  if (!userId || !interviewId || !jobId || !outsourceId || !questionId || !clientAttemptId) return false;
  if (!payload || typeof payload !== 'object') return false;
  if (normalizePositiveInteger(payload.question_id) !== questionId) return false;
  if (normalizeIdentifier(payload.client_attempt_id) !== clientAttemptId) return false;
  if (!normalizeIdentifier(payload.answer_text)) return false;
  if (!Number.isFinite(Number(value.created_at))) return false;
  if (!Number.isFinite(Number(value.updated_at))) return false;
  if (!Number.isFinite(Number(value.expires_at))) return false;
  if (Number(value.expires_at) <= Number(value.created_at)) return false;

  return true;
};

const sanitizePayload = (
  payload: InterviewAnswerRecoveryPayload,
  questionId: number,
  clientAttemptId: string
): InterviewAnswerRecoveryPayload => {
  const sanitized: InterviewAnswerRecoveryPayload = {
    question_id: questionId,
    answer_text: String(payload.answer_text || '').trim(),
    client_attempt_id: clientAttemptId,
    time_spent_seconds: Number(payload.time_spent_seconds) || 0,
  };
  const optionalKeys: Array<keyof InterviewAnswerRecoveryPayload> = [
    'raw_answer',
    'transcript_reviewed',
    'transcript_risk_acknowledged',
    'transcript_quality_score',
    'transcript_review_reason',
    'transcript_quality_flags',
    'transcript_risky_shift_detected',
    'transcript_prefer_raw',
    'transcript_mode',
    'video_url',
    'video_mime_type',
    'video_size_bytes',
    'video_duration_seconds',
    'auto_submit',
    'conversation_mode',
    'seamless_mode',
  ];

  optionalKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      (sanitized as any)[key] = payload[key];
    }
  });
  if (Array.isArray(sanitized.transcript_quality_flags)) {
    sanitized.transcript_quality_flags = sanitized.transcript_quality_flags.map((flag) => String(flag));
  }
  return sanitized;
};

export const buildPendingAnswerRecoveryKey = (userId: string, interviewId: number): string | null => {
  const normalizedUserId = normalizeIdentifier(userId);
  const normalizedInterviewId = normalizePositiveInteger(interviewId);
  if (!normalizedUserId || !normalizedInterviewId) return null;
  return `${INTERVIEW_ANSWER_RECOVERY_PREFIX}${encodeURIComponent(normalizedUserId)}:${normalizedInterviewId}`;
};

const readEnvelopeAtKey = (
  key: string,
  storage: RecoveryStorage,
  now: number
): PendingRecoveryReadResult => {
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return { status: 'missing', envelope: null };
  }
  if (!raw) return { status: 'missing', envelope: null };

  const parsed = safeParse(raw);
  if (!isRecoveryEnvelopeShapeValid(parsed)) {
    removeStorageKey(storage, key);
    return { status: 'invalid', envelope: null };
  }
  if (Number(parsed.expires_at) <= now) {
    removeStorageKey(storage, key);
    return {
      status: 'expired',
      envelope: null,
      scope: {
        userId: parsed.user_id,
        interviewId: parsed.interview_id,
        jobId: parsed.job_id,
        outsourceId: parsed.outsource_id,
        updatedAt: parsed.updated_at,
      },
    };
  }
  return { status: 'valid', envelope: parsed };
};

export const readPendingAnswerRecovery = (
  scope: Pick<PendingInterviewAnswerRecoveryScope, 'userId' | 'interviewId'>,
  options: { storage?: RecoveryStorage; now?: number } = {}
): PendingRecoveryReadResult => {
  const storage = resolveStorage(options.storage);
  const key = buildPendingAnswerRecoveryKey(scope.userId, scope.interviewId);
  if (!storage || !key) return { status: 'missing', envelope: null };
  const result = readEnvelopeAtKey(key, storage, options.now ?? Date.now());
  if (
    result.envelope &&
    (result.envelope.user_id !== normalizeIdentifier(scope.userId) ||
      result.envelope.interview_id !== normalizePositiveInteger(scope.interviewId))
  ) {
    removeStorageKey(storage, key);
    return { status: 'invalid', envelope: null };
  }
  return result;
};

export const readLatestPendingAnswerRecoveryForJob = (
  userId: string,
  jobId: number,
  options: { storage?: RecoveryStorage; now?: number } = {}
): PendingRecoveryReadResult => {
  const storage = resolveStorage(options.storage);
  const normalizedUserId = normalizeIdentifier(userId);
  const normalizedJobId = normalizePositiveInteger(jobId);
  if (!storage || !normalizedUserId || !normalizedJobId) return { status: 'missing', envelope: null };

  const keyPrefix = `${INTERVIEW_ANSWER_RECOVERY_PREFIX}${encodeURIComponent(normalizedUserId)}:`;
  const now = options.now ?? Date.now();
  const inspected = listStorageKeys(storage)
    .filter((key) => key.startsWith(keyPrefix))
    .map((key) => readEnvelopeAtKey(key, storage, now));
  const candidates = inspected
    .filter((result) => result.status === 'valid' && result.envelope?.user_id === normalizedUserId)
    .map((result) => result.envelope as PendingInterviewAnswerRecovery)
    .filter((envelope) => envelope.job_id === normalizedJobId)
    .sort((left, right) => right.updated_at - left.updated_at);

  if (candidates.length) return { status: 'valid', envelope: candidates[0] };

  const expiredScope = inspected
    .filter((result) => result.status === 'expired' && result.scope?.userId === normalizedUserId)
    .map((result) => result.scope!)
    .filter((scope) => scope.jobId === normalizedJobId)
    .sort((left, right) => right.updatedAt - left.updatedAt)[0];
  return expiredScope
    ? { status: 'expired', envelope: null, scope: expiredScope }
    : { status: 'missing', envelope: null };
};

export const persistPendingAnswerRecovery = (
  input: PendingInterviewAnswerRecoveryScope & {
    questionId: number;
    clientAttemptId: string;
    payload: InterviewAnswerRecoveryPayload;
    state?: InterviewAnswerRecoveryState;
  },
  options: { storage?: RecoveryStorage; now?: number; ttlMs?: number } = {}
): PendingInterviewAnswerRecovery => {
  const storage = resolveStorage(options.storage);
  const key = buildPendingAnswerRecoveryKey(input.userId, input.interviewId);
  if (!storage || !key) {
    const error = new Error('PENDING_ANSWER_RECOVERY_STORAGE_UNAVAILABLE');
    error.name = 'PendingAnswerRecoveryStorageError';
    throw error;
  }

  const now = options.now ?? Date.now();
  const ttlMs = Math.max(1, Number(options.ttlMs) || INTERVIEW_ANSWER_RECOVERY_TTL_MS);
  const questionId = normalizePositiveInteger(input.questionId);
  const clientAttemptId = normalizeIdentifier(input.clientAttemptId);
  const userId = normalizeIdentifier(input.userId);
  const existing = safeParse(storage.getItem(key));
  const canPreserveLifetime = isRecoveryEnvelopeShapeValid(existing)
    && existing.user_id === userId
    && existing.interview_id === normalizePositiveInteger(input.interviewId)
    && existing.question_id === questionId
    && existing.client_attempt_id === clientAttemptId;
  const createdAt = canPreserveLifetime ? Number(existing.created_at) : now;
  const expiresAt = canPreserveLifetime ? Number(existing.expires_at) : createdAt + ttlMs;
  const envelope: PendingInterviewAnswerRecovery = {
    version: INTERVIEW_ANSWER_RECOVERY_VERSION,
    state: input.state || 'pending',
    user_id: userId,
    interview_id: normalizePositiveInteger(input.interviewId),
    job_id: normalizePositiveInteger(input.jobId),
    outsource_id: normalizePositiveInteger(input.outsourceId),
    question_id: questionId,
    client_attempt_id: clientAttemptId,
    payload: sanitizePayload(input.payload, questionId, clientAttemptId),
    created_at: createdAt,
    updated_at: now,
    expires_at: expiresAt,
  };

  if (!isRecoveryEnvelopeShapeValid(envelope)) {
    throw new Error('PENDING_ANSWER_RECOVERY_INVALID');
  }
  storage.setItem(key, JSON.stringify(envelope));
  return envelope;
};

export const clearPendingAnswerRecovery = (
  scope: Pick<PendingInterviewAnswerRecoveryScope, 'userId' | 'interviewId'>,
  options: { storage?: RecoveryStorage } = {}
) => {
  const storage = resolveStorage(options.storage);
  const key = buildPendingAnswerRecoveryKey(scope.userId, scope.interviewId);
  if (key) removeStorageKey(storage, key);
};

export const clearAllPendingAnswerRecoveries = (options: { storage?: RecoveryStorage } = {}) => {
  const storage = resolveStorage(options.storage);
  if (!storage) return;
  listStorageKeys(storage)
    .filter((key) => key.startsWith(INTERVIEW_ANSWER_RECOVERY_PREFIX))
    .forEach((key) => removeStorageKey(storage, key));
};

export const clearPendingAnswerRecoveriesForUser = (
  userId: string | null,
  options: { storage?: RecoveryStorage } = {}
) => {
  const storage = resolveStorage(options.storage);
  const normalizedUserId = normalizeIdentifier(userId);
  if (!storage || !normalizedUserId) return;
  const prefix = `${INTERVIEW_ANSWER_RECOVERY_PREFIX}${encodeURIComponent(normalizedUserId)}:`;
  listStorageKeys(storage)
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => removeStorageKey(storage, key));
};

export const clearPendingAnswerRecoveriesForOtherUsers = (
  userId: string | null,
  options: { storage?: RecoveryStorage } = {}
) => {
  const storage = resolveStorage(options.storage);
  const normalizedUserId = normalizeIdentifier(userId);
  if (!storage) return;
  const keepPrefix = normalizedUserId
    ? `${INTERVIEW_ANSWER_RECOVERY_PREFIX}${encodeURIComponent(normalizedUserId)}:`
    : null;
  listStorageKeys(storage)
    .filter((key) => key.startsWith(INTERVIEW_ANSWER_RECOVERY_PREFIX))
    .filter((key) => !keepPrefix || !key.startsWith(keepPrefix))
    .forEach((key) => removeStorageKey(storage, key));
};

export const clearPendingAnswerRecoveriesExcept = (
  scope: Pick<PendingInterviewAnswerRecoveryScope, 'userId' | 'interviewId'>,
  options: { storage?: RecoveryStorage } = {}
) => {
  const storage = resolveStorage(options.storage);
  const keepKey = buildPendingAnswerRecoveryKey(scope.userId, scope.interviewId);
  if (!storage || !keepKey) return;
  listStorageKeys(storage)
    .filter((key) => key.startsWith(INTERVIEW_ANSWER_RECOVERY_PREFIX) && key !== keepKey)
    .forEach((key) => removeStorageKey(storage, key));
};

export const executePersistedAnswerSubmission = async <T,>(
  input: PendingInterviewAnswerRecoveryScope & {
    questionId: number;
    clientAttemptId: string;
    payload: InterviewAnswerRecoveryPayload;
    submit: () => Promise<T>;
  },
  options: { storage?: RecoveryStorage; now?: number; ttlMs?: number } = {}
): Promise<T> => {
  persistPendingAnswerRecovery(input, options);
  try {
    const result = await input.submit();
    clearPendingAnswerRecovery(input, options);
    return result;
  } catch (error: any) {
    if (error?.name === 'SubmitRecoveryFailure' && error?.retryable === false) {
      clearPendingAnswerRecovery(input, options);
    } else {
      persistPendingAnswerRecovery({ ...input, state: 'uncertain' }, options);
    }
    throw error;
  }
};

export const resolvePendingAnswerRestoreDecision = (
  envelope: PendingInterviewAnswerRecovery,
  backend: PendingRecoveryBackendSnapshot
): PendingRecoveryRestoreDecision => {
  const questionId = Number(envelope.question_id);
  const questionBelongs = backend.questionIds.some((id) => Number(id) === questionId);
  const alreadyAnswered = backend.answeredQuestionIds.some((id) => Number(id) === questionId);

  if (alreadyAnswered) return { action: 'stale_success', consumeNoAnswerAttempt: false };
  if (backend.interviewCompleted || backend.canFinish) {
    return { action: 'stale_success', consumeNoAnswerAttempt: false };
  }
  if (!questionBelongs || Number(backend.currentQuestionId) !== questionId) {
    return { action: 'discard', consumeNoAnswerAttempt: false };
  }
  return { action: 'reconcile', consumeNoAnswerAttempt: false };
};

export type PendingAnswerRestoreCoordinator = {
  execute: <T>(key: string, operation: () => Promise<T>) => Promise<T>;
  hasActive: (key: string) => boolean;
};

export const createPendingAnswerRestoreCoordinator = (): PendingAnswerRestoreCoordinator => {
  const active = new Map<string, Promise<any>>();
  return {
    execute<T>(key: string, operation: () => Promise<T>): Promise<T> {
      const existing = active.get(key);
      if (existing) return existing as Promise<T>;
      const promise = operation().finally(() => {
        if (active.get(key) === promise) active.delete(key);
      });
      active.set(key, promise);
      return promise;
    },
    hasActive: (key: string) => active.has(key),
  };
};

export const pendingAnswerRestoreCoordinator = createPendingAnswerRestoreCoordinator();
